import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Firebase Storage Service
 * Handles file uploads, downloads, and deletions
 */

/**
 * Upload file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - Storage path (e.g., 'jobs_uploads/userId/jobId/filename')
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<string>} - Download URL of uploaded file
 */
export const uploadFile = async (file, path, onProgress = null) => {
  try {
    const storageRef = ref(storage, path);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

export const uploadPrivateFile = async (file, folder, jobId = null) => {
  const form = new FormData();
  form.append('file', file);
  form.append('folder', folder);
  if (jobId) form.append('job_id', jobId);
  const response = await axios.post(`${API}/private-files/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.reference;
};

/**
 * Delete file from Firebase Storage
 * @param {string} path - Storage path of file to delete
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(`Delete failed: ${error.message}`);
  }
};

/**
 * Generate unique file path
 * @param {string} folder - Folder name (jobs_uploads, profiles, verification_docs)
 * @param {string} userId - User ID
 * @param {string} filename - Original filename
 * @param {string} subfolder - Optional subfolder (e.g., jobId)
 * @returns {string} - Full storage path
 */
export const generateFilePath = (folder, userId, filename, subfolder = null) => {
  const timestamp = Date.now();
  const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
  
  if (subfolder) {
    return `${folder}/${userId}/${subfolder}/${timestamp}_${sanitizedFilename}`;
  }
  
  return `${folder}/${userId}/${timestamp}_${sanitizedFilename}`;
};

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = async (file, userId) => {
  const path = generateFilePath('profiles', userId, file.name);
  return await uploadFile(file, path);
};

/**
 * Upload company logo
 */
export const uploadCompanyLogo = async (file, userId) => {
  const path = generateFilePath('profiles', userId, `logo_${file.name}`);
  return await uploadFile(file, path);
};

/**
 * Upload verification document
 */
export const uploadVerificationDoc = async (file, userId, docType) => {
  return await uploadPrivateFile(file, 'verification_docs');
};

/**
 * Upload job photo
 */
export const uploadJobPhoto = async (file, userId, jobId) => {
  return await uploadPrivateFile(file, 'jobs_uploads', jobId);
};

export default {
  uploadFile,
  uploadPrivateFile,
  deleteFile,
  generateFilePath,
  uploadProfilePhoto,
  uploadCompanyLogo,
  uploadVerificationDoc,
  uploadJobPhoto
};
